"use client";

import Link from "next/link";
import Image from "next/image";
import { ArrowUpRight } from "lucide-react";
import { DEV_RESOURCES, EXTERNAL_LINKS } from "@/lib/constants";

const footerLinks = {
  product: [
    { name: "Shopping Assistant", href: "/shopping-assistant" },
    { name: "Semantic Search", href: "/semantic-search" },
    { name: "Product Intelligence", href: "/product-intelligence" },
    { name: "Assistant Management", href: "/assistant-management" },
    { name: "Assistant Extensions", href: "/assistant-extensions" },
  ],
  services: [
    { name: "Integrations", href: "/integrations" },
    { name: "Custom Integration", href: "/custom-integration" },
    { name: "Pricing", href: "/pricing" },
    { name: "Contact Sales", href: "/contact" },
  ],
  company: [
    { name: "About Us", href: "/about" },
    { name: "Security", href: "/security" },
    { name: "Contact", href: "/contact" },
  ],
  legal: [
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms of Service", href: "/terms" },
    { name: "Imprint", href: "/imprint" },
  ],
};

const socialLinks = [
  { name: "LinkedIn", href: EXTERNAL_LINKS.linkedin },
  { name: "Twitter", href: EXTERNAL_LINKS.twitter },
  { name: "GitHub", href: EXTERNAL_LINKS.github },
];

export default function Footer() {
  return (
    <footer className="relative border-t border-[var(--border-subtle)] overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-bg" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-accent/[0.02] rounded-full blur-[150px]" />

      <div className="container relative z-10">
        {/* Main Footer */}
        <div className="py-16 grid grid-cols-2 md:grid-cols-6 gap-8">
          {/* Brand Column */}
          <div className="col-span-2">
            <Link href="/" className="flex items-center gap-3 mb-6 group">
              <Image
                src="/images/logo.svg"
                alt="IsarTech Logo"
                width={48}
                height={48}
                className="h-8 w-8 sm:h-10 sm:w-10"
              />
              <span className="text-xl font-bold text-fg">IsarTech</span>
            </Link>
            <p className="text-sm text-fg-subtle mb-6 max-w-xs">
              Agentic AI that searches, recommends, and sells, embedded in your store. Built in Germany and Austria.
            </p>
            <div className="flex items-center gap-2">
              {socialLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-fg-subtle hover:text-accent transition-colors text-sm py-2 px-2"
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="text-sm font-semibold text-fg mb-4">Product</h4>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.name}>
                  <Link
                    href={link.href}
                    className="text-sm text-fg-subtle hover:text-fg transition-colors py-2.5 inline-block"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="text-sm font-semibold text-fg mb-4">Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    href={link.href}
                    className="text-sm text-fg-subtle hover:text-fg transition-colors py-2.5 inline-block"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-sm font-semibold text-fg mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    href={link.href}
                    className="text-sm text-fg-subtle hover:text-fg transition-colors py-2.5 inline-block"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-sm font-semibold text-fg mb-4">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link
                    href={link.href}
                    className="text-sm text-fg-subtle hover:text-fg transition-colors py-2.5 inline-block"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Developer Resources */}
        <div className="py-8 border-t border-[var(--border-subtle)]">
          <div className="flex flex-wrap items-center gap-6">
            <span className="text-sm text-fg-subtle">Developer Resources:</span>
            {DEV_RESOURCES.map((resource) => (
              <a
                key={resource.name}
                href={resource.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-fg-muted hover:text-accent transition-colors py-2"
              >
                {resource.name}
                {resource.showStatusDot ? (
                  <span className="w-2 h-2 bg-success rounded-full" />
                ) : (
                  <ArrowUpRight className="w-3 h-3" />
                )}
              </a>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-[var(--border-subtle)] flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-6 text-sm text-fg-subtle">
            <span>&copy; 2026 IsarTech Company. All rights reserved.</span>
            <span className="hidden md:inline">&bull;</span>
            <span className="flex items-center gap-2">
              Made with care in
              <span className="text-fg">Germany</span> &
              <span className="text-fg">Austria</span>
            </span>
          </div>
          <Link
            href="/contact"
            className="btn-primary text-sm px-6 py-2.5"
          >
            Book Demo
          </Link>
        </div>
      </div>
    </footer>
  );
}
